import {
    Table,
    TableBody,
    TableCell,
    TableCellStack,
    TableHead,
    TableHeader,
    TableNum,
    TableRow,
} from '@/components/Table/Table';
import { transactionsExportUrl, type TransactionsReport } from '@/features/reports/api';
import { TRANSACTIONS_COPY } from '@/features/reports/copy';
import { formatPercent, formatPounds } from '@/features/reports/format';
import { ReportCard } from '@/features/reports/parts';
import { type ReportPeriod } from '@/features/reports/periods';
import { ExportButton } from '@/features/reports/transactions/ExportButton';
import { exportFileName } from '@/features/reports/transactions/labels';
import { cn } from '@/util/utils';

const COPY = TRANSACTIONS_COPY.products;

/**
 * Transactions › Products (ALS-86): each product's margin by the line rule, less its refunds
 * settled in the period, as an amount and as a share of its net. Sorted by what it made.
 */
export function ProductsView({
    report,
    period,
}: {
    report: TransactionsReport;
    period: ReportPeriod;
}) {
    const products = report.products;

    return (
        <ReportCard
            title={COPY.title}
            subtitle={COPY.subtitle(report.current.label)}
            actions={
                <ExportButton
                    url={transactionsExportUrl('Products', period)}
                    fileName={exportFileName(['products', period])}
                    disabled={products.length === 0}
                />
            }
        >
            <p className="text-muted-foreground pb-3 text-xs">{COPY.note}</p>
            {products.length === 0 ? (
                <p className="text-muted-foreground py-4 text-sm">{COPY.empty}</p>
            ) : (
                <Table stack region={COPY.title} className="table-stack--wide-labels">
                    <TableHeader>
                        <TableRow>
                            <TableHead>{COPY.columns.product}</TableHead>
                            <TableHead className="text-right">{COPY.columns.units}</TableHead>
                            <TableHead className="text-right">{COPY.columns.net}</TableHead>
                            <TableHead className="text-right">{COPY.columns.before}</TableHead>
                            <TableHead className="text-right">{COPY.columns.refunds}</TableHead>
                            <TableHead className="text-right">{COPY.columns.after}</TableHead>
                            <TableHead className="text-right">{COPY.columns.percentage}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {products.map((p) => (
                            <TableRow key={p.productId}>
                                <TableCell data-stack="title">
                                    <TableCellStack>
                                        <span className="font-medium">{p.name}</span>
                                        {p.brand ? (
                                            <span className="text-xs">{p.brand}</span>
                                        ) : null}
                                    </TableCellStack>
                                </TableCell>
                                <TableNum data-stack="meta" data-label={COPY.columns.units}>
                                    {p.units}
                                </TableNum>
                                <TableNum data-stack="meta" data-label={COPY.columns.net}>
                                    {formatPounds(p.net)}
                                </TableNum>
                                <TableNum data-stack="meta" data-label={COPY.columns.before}>
                                    {formatPounds(p.marginBeforeRefunds)}
                                </TableNum>
                                <TableNum data-stack="meta" data-label={COPY.columns.refunds}>
                                    {p.refunds === 0
                                        ? formatPounds(0)
                                        : `-${formatPounds(p.refunds)}`}
                                </TableNum>
                                <TableNum
                                    data-stack="meta"
                                    data-label={COPY.columns.after}
                                    className={cn(
                                        'font-medium',
                                        p.marginAfterRefunds < 0 && 'text-destructive',
                                    )}
                                >
                                    {formatPounds(p.marginAfterRefunds)}
                                </TableNum>
                                <TableNum data-stack="status">
                                    {p.marginPercentage === null
                                        ? '–'
                                        : formatPercent(p.marginPercentage)}
                                </TableNum>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </ReportCard>
    );
}
